import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Select,
  Switch,
  Button,
  Space,
  Divider,
  message,
} from 'antd';
import { FilterOutlined, ClearOutlined, CloseOutlined } from '@ant-design/icons';
import { Event, GiftGiver } from '../../services/giftService';
import styles from '../common/DrawerModalMobile.module.css';

const { Option } = Select;

interface FilterModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: any) => void;
  currentFilters: any;
  events: Event[];
  givers: GiftGiver[];
}

export const FilterModalMobile: React.FC<FilterModalMobileProps> = ({
  isOpen,
  onClose,
  onApply,
  currentFilters,
  events,
  givers,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        event_id: currentFilters.event_id,
        giver_id: currentFilters.giver_id,
        gift_type: currentFilters.gift_type,
        is_returned: currentFilters.is_returned,
      });
      checkActiveFilters(currentFilters);
    }
  }, [isOpen, currentFilters, form]);

  const checkActiveFilters = (filters: any) => {
    const hasFilters = Boolean(
      filters.event_id || 
      filters.giver_id || 
      filters.gift_type || 
      filters.is_returned !== undefined
    );
    setHasActiveFilters(hasFilters);
  };

  const handleApply = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Chuyển đổi giá trị boolean
      if (values.is_returned !== undefined) {
        values.is_returned = values.is_returned === true;
      }
      
      onApply(values);
      onClose();
      message.success('Đã áp dụng bộ lọc');
    } catch (error) {
      console.error('Filter validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue({
      event_id: undefined,
      giver_id: undefined,
      gift_type: undefined,
      is_returned: undefined,
    });
    // Áp dụng filter rỗng ngay lập tức
    onApply({
      event_id: undefined,
      giver_id: undefined,
      gift_type: undefined,
      is_returned: undefined,
    });
    onClose();
    message.success('Đã xóa bộ lọc');
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    checkActiveFilters(values);
  };

  return (
    <Drawer
      title="Bộ lọc quà tặng"
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="70%"
      className={styles.drawer}
      bodyStyle={{ padding: 0 }}
      headerStyle={{ 
        borderBottom: '1px solid #f0f0f0',
        padding: '16px 20px'
      }}
    >
      <div className={styles.drawerContent}>
        <div className={styles.drawerBody}>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleFormChange}
          >
            <div className={styles.formItem}>
              <Form.Item
                label="Sự kiện"
                name="event_id"
              >
                <Select
                  placeholder="Chọn sự kiện"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  className={styles.select}
                >
                  {events.map((event) => (
                    <Option key={event.id} value={event.id}>
                      {event.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                label="Người tặng"
                name="giver_id"
              >
                <Select
                  placeholder="Chọn người tặng"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  className={styles.select}
                >
                  {givers.map((giver) => (
                    <Option key={giver.id} value={giver.id}>
                      {giver.full_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                label="Loại quà"
                name="gift_type"
              >
                <Select
                  placeholder="Chọn loại quà"
                  allowClear
                  className={styles.select}
                >
                  <Option value="cash">Tiền mặt</Option>
                  <Option value="gift">Quà tặng</Option>
                  <Option value="other">Khác</Option>
                </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                label="Trạng thái đáp lễ"
                name="is_returned"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Đã đáp lễ"
                  unCheckedChildren="Chưa đáp lễ"
                />
              </Form.Item>
            </div>
          </Form>
        </div>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterRow}>
            <Button
              className={`${styles.drawerFooterButton} default`}
              onClick={onClose}
              icon={<CloseOutlined />}
            >
              Đóng
            </Button>
            {hasActiveFilters && (
              <Button
                className={`${styles.drawerFooterButton} default`}
                onClick={handleReset}
                icon={<ClearOutlined />}
              >
                Xóa bộ lọc
              </Button>
            )}
            <Button
              type="primary"
              className={`${styles.drawerFooterButton} primary`}
              onClick={handleApply}
              loading={loading}
              icon={<FilterOutlined />}
            >
              Áp dụng
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}; 